import { MenuItem as ChakraMenuItem } from '@chakra-ui/react-v2';
import type { MenuItemProps as ChakraMenuItemProps } from '@chakra-ui/react-v2';
import { forwardRef } from '@chakra-ui/system';
import { cloneElement } from 'react';

import { useMenuContext } from './utils/MenuContext';

export type MenuItemProps = ChakraMenuItemProps & {
  colorScheme?: 'destructive';
};

export const MenuItem = forwardRef<MenuItemProps, 'button'>(
  (
    {
      icon,
      isDisabled,
      colorScheme,
      children,
      onClick,
      onSelect,
      ...restProps
    },
    ref,
  ) => {
    const { size } = useMenuContext();
    const defaultIconColor = 'var(--chakra-colors-feature-secondary)';
    const disabledColor = 'var(--chakra-colors-interaction-disabled)';
    const destructiveColor = 'var(--chakra-colors-interaction-destructive)';
    const isDestructive = colorScheme === 'destructive';

    let realIcon;
    if (icon) {
      const { color, ...iconProps } = icon.props;
      const iconColor = isDisabled
        ? disabledColor
        : color ?? (isDestructive ? destructiveColor : defaultIconColor);

      realIcon = cloneElement(icon, {
        size: '1.25rem',
        style: {
          color: iconColor,
          marginInlineEnd: '-0.25rem',
          marginTop: '-0.125rem',
          ...iconProps,
        },
      });
    }
    return (
      <ChakraMenuItem
        size={size}
        icon={realIcon}
        {...(isDisabled && {
          'data-disabled': true,
          closeOnSelect: false,
        })}
        onClick={(e) => {
          if (isDisabled) {
            e.preventDefault();
          } else {
            onClick?.(e);
          }
        }}
        ref={ref}
        color={isDestructive ? destructiveColor : undefined}
        _hover={{
          color: isDestructive && !isDisabled ? destructiveColor : undefined,
        }}
        {...restProps}
      >
        {children}
      </ChakraMenuItem>
    );
  },
);
MenuItem.displayName = 'MenuItem';
